import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { validateTanCartIntegrity } from '@theme/tan-cart-integrity';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

let tanCartReconciledSignature = '';

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;
  /** @type {boolean} */
  #checkoutValidationInProgress = false;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {{items?: Array<Record<string, any>>}} cart */
  #validateTanCartIntegrity(cart) {
    return validateTanCartIntegrity(cart);
  }

  /** @param {string} [message] */
  #setTanCartIntegrityError(message = '') {
    const errorElement = this.querySelector('[data-tan-cart-integrity-error]');
    if (!(errorElement instanceof HTMLElement)) return;

    errorElement.hidden = !message;
    if (message) errorElement.textContent = message;
  }

  /**
   * Returns a locale-backed checkout integrity message rendered by Liquid.
   * Falls back to the existing server-rendered integrity error if the expected
   * data attribute is unavailable after a third-party cart morph.
   * @param {string} attributeName
   */
  #getTanCartIntegrityMessage(attributeName) {
    const errorElement = this.querySelector('[data-tan-cart-integrity-error]');
    if (!(errorElement instanceof HTMLElement)) return '';

    return errorElement.getAttribute(attributeName)?.trim() || errorElement.textContent?.trim() || '';
  }

  /** @param {SubmitEvent} event */
  #handleCheckoutSubmit = (event) => {
    const submitter = event.submitter;
    if (!(event.target instanceof HTMLFormElement)) return;
    if (!(submitter instanceof HTMLButtonElement) || submitter.name !== 'checkout') return;

    const hasProtectedCustomization =
      this.querySelector('[data-tan-cart-link][data-tan-protected="true"]') !== null;
    if (!hasProtectedCustomization) return;

    event.preventDefault();
    if (this.#checkoutValidationInProgress) return;

    this.#checkoutValidationInProgress = true;
    submitter.disabled = true;
    submitter.setAttribute('aria-busy', 'true');
    this.#setTanCartIntegrityError();

    void this.fetchCartData()
      .then((cart) => {
        if (!this.#validateTanCartIntegrity(cart)) {
          this.#setTanCartIntegrityError(
            this.#getTanCartIntegrityMessage('data-tan-cart-integrity-invalid')
          );
          return;
        }

        const shopRoot = String(window.Shopify?.routes?.root || '/');
        const rootWithSlash = shopRoot.endsWith('/') ? shopRoot : `${shopRoot}/`;
        window.location.assign(`${rootWithSlash}checkout`);
      })
      .catch((error) => {
        console.warn('[cart-items] TAN checkout validation failed:', error);
        this.#setTanCartIntegrityError(
          this.#getTanCartIntegrityMessage('data-tan-cart-integrity-check-failed')
        );
      })
      .finally(() => {
        this.#checkoutValidationInProgress = false;
        submitter.disabled = false;
        submitter.removeAttribute('aria-busy');
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    this.addEventListener('submit', this.#handleCheckoutSubmit);

    void this.#reconcileTanCartGroups();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    this.removeEventListener('submit', this.#handleCheckoutSubmit);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#getCartItemRow(line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.#getCartItemRow(line);

    if (!cartItemRowToRemove) return;

    const configurationId = cartItemRowToRemove.dataset.tanConfiguration || '';
    const customizationSummary = configurationId
      ? this.querySelector(`[data-tan-summary-for="${CSS.escape(configurationId)}"]`)
      : null;
    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
      ...(customizationSummary instanceof HTMLElement ? [customizationSummary] : []),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = this.refs.cartItemRows.length === 1;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const lineItemRow = this.#getCartItemRow(line);
    let lineId = lineItemRow?.dataset.key ?? '';
    let feeLineId = lineItemRow?.dataset.tanFeeKey ?? '';

    // Resolve the relationship once more from Shopify's live cart at action
    // time. This is authoritative even if a stale section or an external cart
    // mutation left the rendered data attributes incomplete.
    try {
      const liveCart = await this.fetchCartData();
      const liveMainItem =
        liveCart.items?.find((item) => String(item?.key || '') === lineId) || liveCart.items?.[line - 1];
      if (liveMainItem) {
        lineId = String(liveMainItem.key || lineId);
        const configurationId = String(liveMainItem.properties?.['_TAN configuration'] || '');
        if (configurationId) {
          const liveFeeItem = liveCart.items?.find(
            (item) =>
              item?.properties?.['_TAN fee'] === 'true' &&
              String(item?.properties?.['_TAN configuration'] || '') === configurationId
          );
          if (liveFeeItem?.key) feeLineId = String(liveFeeItem.key);
        }
      }
    } catch (error) {
      console.warn('[cart-items] Live TAN cart relationship lookup failed; using rendered relationship:', error);
    }

    /** @type {Record<string, number>} */
    const updates = {};
    if (lineId) updates[lineId] = quantity;
    if (feeLineId) updates[feeLineId] = quantity;

    const body = JSON.stringify({
      updates,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    const eventLines = [{ id: lineId, quantity }];
    if (feeLineId) eventLines.push({ id: feeLineId, quantity });
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: eventLines,
        promise: deferredUpdatePromise.promise,
      })
    );

    fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
        });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
        deferredUpdatePromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.#getCartItemRow(line)?.querySelector('cart-quantity-selector-component');
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Finds a visible cart row by Shopify's real 1-based cart line number. TAN fee
   * lines are intentionally hidden from `cartItemRows`, so array positions are
   * not reliable once more than one customized product is present.
   * @param {number} line
   * @returns {HTMLTableRowElement | undefined}
   */
  #getCartItemRow(line) {
    return this.refs.cartItemRows.find((row) => Number(row.dataset.line) === Number(line));
  }

  /**
   * Repairs legacy or externally-created TAN cart mismatches. The relationship
   * data is rendered with the cart section, so a healthy cart needs no request.
   * Only orphaned fees or quantity mismatches trigger one atomic update.
   */
  async #reconcileTanCartGroups() {
    const links = Array.from(this.querySelectorAll('[data-tan-cart-link]'))
      .filter((link) => link instanceof HTMLElement)
      .map((link) => ({
        key: link.dataset.key || '',
        configuration: link.dataset.tanConfiguration || '',
        quantity: Math.max(0, Number(link.dataset.quantity) || 0),
        variantId: link.dataset.variantId || '',
        expectedFeeVariant: link.dataset.tanExpectedFeeVariant || '',
        isFee: link.dataset.tanIsFee === 'true',
      }))
      .filter((link) => link.key && link.configuration);

    const signature = links
      .map(
        (link) =>
          `${link.key}:${link.configuration}:${link.quantity}:${link.variantId}:${link.expectedFeeVariant}:${
            link.isFee ? 'fee' : 'main'
          }`
      )
      .sort()
      .join('|');
    if (signature === tanCartReconciledSignature) return;
    tanCartReconciledSignature = signature;

    const mainByConfiguration = new Map(
      links.filter((link) => !link.isFee).map((link) => [link.configuration, link])
    );
    const feeLinesByConfiguration = new Map();
    for (const feeLine of links.filter((link) => link.isFee)) {
      const existingFeeLines = feeLinesByConfiguration.get(feeLine.configuration) || [];
      existingFeeLines.push(feeLine);
      feeLinesByConfiguration.set(feeLine.configuration, existingFeeLines);
    }

    /** @type {Record<string, number>} */
    const updates = {};

    for (const [configuration, feeLines] of feeLinesByConfiguration) {
      const mainLine = mainByConfiguration.get(configuration);
      if (!mainLine) {
        feeLines.forEach((feeLine) => {
          updates[feeLine.key] = 0;
        });
        continue;
      }

      const exactExpectedFee = mainLine.expectedFeeVariant
        ? feeLines.find((feeLine) => feeLine.variantId === mainLine.expectedFeeVariant)
        : null;
      const retainedFeeLine = exactExpectedFee || feeLines[0];

      for (const feeLine of feeLines) {
        if (feeLine.key !== retainedFeeLine.key) {
          updates[feeLine.key] = 0;
        } else if (mainLine.quantity !== feeLine.quantity) {
          updates[feeLine.key] = mainLine.quantity;
        }
      }
    }

    if (Object.keys(updates).length === 0) return;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set();
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionsToUpdate.add(item.dataset.sectionId);
    });

    const body = JSON.stringify({
      updates,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });
    const lines = Object.entries(updates).map(([id, quantity]) => ({ id, quantity }));
    const updatePromise = fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body }))
      .then((response) => response.json())
      .then((response) => {
        if (response.errors) throw new Error(String(response.errors));
        const visibleItemCount = Array.isArray(response.items)
          ? response.items.reduce(
              (total, item) =>
                item?.properties?.['_TAN fee'] === 'true' ? total : total + (Number(item?.quantity) || 0),
              0
            )
          : 0;
        return {
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(response),
          detail: {
            sections: response.sections,
            items: response.items,
            itemCount: visibleItemCount,
            source: 'tan-cart-reconciliation',
            didError: false,
          },
        };
      });

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: lines.every((line) => line.quantity === 0) ? 'remove' : 'update',
        context: 'cart',
        lines,
        promise: updatePromise,
      })
    );

    try {
      await updatePromise;
    } catch (error) {
      tanCartReconciledSignature = '';
      console.warn('[cart-items] TAN cart group reconciliation failed:', error);
    }
  }

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
